import { useGetCheckoutLinksPlanByCheckoutLinkId } from "@birdwatch/client";
import { useMemo } from "react";

/** The membership a campaign link sells, priced the way the buyer will be charged. */
export type PresaleMembership = {
  /** Display name, e.g. "Core Membership". */
  name: string;
  /** `Plan.key` — the same value the join funnel sends as `planKey`. */
  planKey: string;
  /** Stripe-formatted amount, e.g. "$0.00". `null` when the price failed to resolve. */
  formattedPrice: string | null;
  /** Unit price in CENTS, as Stripe holds it — the unit `Join.planValue` carries. */
  unitAmountCents: number | null;
  /** `"month"` — the interval the price renews on. */
  interval: string | null;
};

/**
 * The membership the account step is reserving.
 *
 * Reads `GET /checkout-links/:id/plan` — the only PUBLIC checkout-link endpoint, and
 * step 1 is signed out, so the auth-gated `/:checkoutLinkId` detail route would 401
 * here. That endpoint also owns "which line item is the plan" and applies the link's
 * own price override server-side, so this hook re-derives neither.
 *
 * No `checkoutLinkId` means no offer to show: the query stays disabled and the caller
 * renders nothing. A price shown against the wrong link is worse than no price.
 */
export function usePresaleMembership(checkoutLinkId: string | undefined): {
  data: PresaleMembership | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useGetCheckoutLinksPlanByCheckoutLinkId({
    checkoutLinkId: checkoutLinkId ?? "",
  }).get({ enabled: !!checkoutLinkId });

  // Memoized so the returned object is referentially STABLE across renders. Callers put it
  // in `useMemo` / `useCallback` dependency arrays (the checkout step's analytics payload
  // does), and a fresh literal each render would silently defeat every one of them.
  const membership = useMemo(
    () =>
      data
        ? {
            name: data.plan,
            planKey: data.planKey,
            formattedPrice: data.price?.formattedPrice ?? null,
            unitAmountCents: data.price?.unitAmount ?? null,
            interval: data.price?.interval ?? data.billingCycle ?? null,
          }
        : null,
    [data],
  );

  return {
    data: membership,
    // Same guard as `useJoinPlan` (use-join-plan.ts:60): a disabled query must never
    // report loading, or the caller's `&&` gate stops being the only one.
    isLoading: !!checkoutLinkId && isLoading,
  };
}
